import React from 'react';
import { useIntl } from 'react-intl';
import { Alert } from 'reactstrap';
import { TypedMemo } from '../common/types';
import { SMLButton } from '../common/views/SMLButton/SMLButton';
import { useUserTokenDecoded } from '../../redux/slices/appInfo/hooks';
import { PhoneConnectStatus } from './usePhoneConnectWatch';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole handoff; each part uses its own slice of it */
import styles from './MobileApps.module.scss';
/* eslint-enable css-modules/no-unused-class */

const bold = (chunks: React.ReactNode[]) => <b>{chunks}</b>;

interface PhoneConnectStatusRowProps {
  status: Exclude<PhoneConnectStatus, 'connected'>;
  onCheckAgain: () => void;
}

/** The row under the handoff card: waiting (design 1a) or still waiting after ten minutes (2c). */
const PhoneConnectStatusRowComponent: React.FC<PhoneConnectStatusRowProps> = ({ status, onCheckAgain }) => {
  const intl = useIntl();
  const email = useUserTokenDecoded()?.email ?? '';

  if (status === 'timeout') {
    return (
      <>
        <Alert color="info" className="mb-0">
          {intl.formatMessage(
            {
              id: 'app.sml.mobileApps.status.timeoutCatalog',
              defaultMessage:
                '<b>No connection yet.</b> Make sure the app is signed in as {email}, then tap Connect in Catalog.',
            },
            { email, b: bold }
          )}
        </Alert>
        <div className={styles.actions}>
          <SMLButton outline onClick={onCheckAgain}>
            {intl.formatMessage({ id: 'app.sml.mobileApps.status.checkAgain', defaultMessage: 'Check again' })}
          </SMLButton>
        </div>
      </>
    );
  }

  return (
    <div className={styles.status} role="status">
      <span className={styles.live} aria-hidden="true" />
      <div className={styles.statusText}>
        <b>
          {intl.formatMessage({ id: 'app.sml.mobileApps.status.waiting', defaultMessage: 'Waiting for your phone' })}
        </b>
        <small>
          {intl.formatMessage({
            id: 'app.sml.mobileApps.status.waitingNoteStreaming',
            defaultMessage: 'You can close this window. Streaming shows the data when the connection is done.',
          })}
        </small>
      </div>
    </div>
  );
};

export const PhoneConnectStatusRow = TypedMemo(PhoneConnectStatusRowComponent);
